import { useId, type ReactNode } from 'react';
import { t } from '@app/i18n';
import { InfoTip } from './InfoTip';
import s from './Card.module.css';

export interface CardProps {
  title?: ReactNode;
  /** Texto pequeno ao lado do título (contagem, "n de m"). */
  subtitle?: ReactNode;
  /** Explicação ⓘ ao lado do título (FR-132). */
  tip?: string;
  /** Ações do canto direito do cabeçalho. */
  toolbar?: ReactNode;
  /** `true` = corpo sem padding (usado por tabelas, que já têm o seu). */
  bare?: boolean;
  /** `true` = card estica para ocupar a altura da linha do grid. */
  fill?: boolean;
  className?: string;
  children?: ReactNode;
  /** Rótulo acessível quando não há título visível. */
  'aria-label'?: string;
}

/** Card (`.card`) com cabeçalho, toolbar e corpo. */
export function Card({
  title,
  subtitle,
  tip,
  toolbar,
  bare,
  fill,
  className,
  children,
  'aria-label': ariaLabel,
}: CardProps) {
  const hasHeader = Boolean(title || subtitle || toolbar);
  const titleId = useId();

  return (
    <section
      className={[s.card, fill ? s.fill : '', className].filter(Boolean).join(' ')}
      aria-label={ariaLabel}
      // Um `<section>` só vira região navegável quando tem nome acessível. Com
      // título, o próprio `<h2>` é o nome — leitor de tela pula de card em card, e
      // o teste consegue escopar as buscas a um card só.
      aria-labelledby={!ariaLabel && title ? titleId : undefined}
    >
      {hasHeader ? (
        <div className={s.header}>
          {title ? (
            <h2 className={s.title} id={titleId}>
              {title}
              {tip ? <InfoTip text={tip} label={t('common.ui.about.card')} /> : null}
            </h2>
          ) : null}
          {subtitle ? <span className={s.sub}>{subtitle}</span> : null}
          {toolbar ? <div className={s.toolbar}>{toolbar}</div> : null}
        </div>
      ) : null}
      <div
        className={[s.body, bare ? s.bare : '', hasHeader ? s.bodyTop : '']
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </div>
    </section>
  );
}
